import * as L from "leaflet";
import {UIComponent} from "@/engine/UI/UIComponent.js";
import {DebugInfoCollector} from "@/engine/deck/DebugInfoCollector.js";
import {DebugInfoPanel} from "@/engine/deck/DebugInfoPanel.js";
import {UICVerticalToolBar} from "@/engine/UIcomponents/UICVerticalToolBar.js";
import {CAMERA_MODE} from "@/engine/deck/CameraMode.js";
import {
    LAYER_3D_SOURCE_TYPE_3DTILES,
    LAYER_3D_SOURCE_TYPE_3DTILES_11,
} from "@/engine/layers/layer3D.js";

const RENDER_TYPE_DECK = 0
const RENDER_TYPE_THREE = 1

export class UIC3DViewer extends UIComponent {
    /**
     * Creates a UI component that opens a 3D object demo viewer in a dialog.
     *
     * @param {{layer?: object, sourceUrl?: string, sourceType?: string, sceneType?: string, zeroLevel?: number|string|object}} [options] Viewer options.
     * @param {object} [options.layer] Layer object that exposes 3D source and scene metadata.
     * @param {string} [options.sourceUrl] Fallback direct source URL for temporary compatibility.
     * @param {string} [options.sourceType] Fallback direct source type for temporary compatibility.
     * @param {string} [options.sceneType] Fallback direct scene type for temporary compatibility.
     * @param {number|string|object} [options.zeroLevel] Fallback fixed scene zero level for reference helpers.
     * @returns {UIC3DViewer} Initialized UI component instance.
     */
    constructor(options) {
        super(options, createContent);
        const layer = options?.layer || null;
        this.id = "threeDViewer";
        this.title = layer?.name ? `3D object: ${layer.name}` : "3D object demo";
        this.className = "threeDViewerWindow";
        this.cancelActionName = "CLOSE";
        this.mainActionName = "";
        this.lookupElements = ["div", "span"];
        this.layer = layer;
        this.sourceUrl = layer?.get3DSourceUrl?.() || options?.sourceUrl || null;
        this.sourceType = layer?.get3DSourceType?.() || options?.sourceType || null;
        this.sceneType = layer?.get3DSceneType?.() || options?.sceneType || null;
        this.zeroLevel = layer?.get3DZeroLevel?.() ?? options?.zeroLevel ?? null;
        this.viewer = null;
        this.rendererType = null;
        this.loading = null;
        this.debugCollapsed = true;
        this.debugCollector = new DebugInfoCollector();
        this.debugPanel = null;
        this.toolbar = null;
    }

    /**
     * Mounts component content, creates the selected viewer, and starts loading the model.
     *
     * @param {HTMLElement|L.Control.Window} container Parent container or Leaflet window.
     * @param {object} [parent] Optional parent UI component/controller.
     * @param {object} [data] Optional template data passed by UIComponent.
     * @param {object} [templateIncludes] Optional Mustache partials passed by UIComponent.
     * @returns {void}
     */
    open(container, parent, data, templateIncludes) {
        super.open(container, parent, data, templateIncludes);
        const viewport = this.el("viewport");
        const viewportFrame = this.el("viewportFrame");

        if (!viewport) {
            this._setError(new Error("3D viewer viewport was not created"));
            return;
        }

        this._disableMapScroll(viewportFrame || viewport);
        this._initDebugPanel();
        this._syncDebugCollapsedState();
        this._createViewer(viewport)
            .then((viewer) => {
                this.viewer = viewer;
                this._initToolbar(viewer);
                this.layer.addOnMap3D(viewer, null);
                this._loadObject();
            })
            .catch((error) => this._setError(error));
    }

    /**
     * Creates a deck.gl or Three.js viewer according to the configured source URL.
     *
     * @param {HTMLElement} viewport Element that receives the renderer canvas.
     * @returns {Promise<object>} Viewer instance for the active source.
     */
    async _createViewer(viewport) {
        this.rendererType = this.sourceType === LAYER_3D_SOURCE_TYPE_3DTILES || this.sourceType === LAYER_3D_SOURCE_TYPE_3DTILES_11
            ? RENDER_TYPE_DECK
            : RENDER_TYPE_THREE;

        if (this.rendererType === RENDER_TYPE_DECK) {
            const {Deck3DTilesViewer} = await import("@/engine/deck/Deck3DTilesViewer.js");

            return new Deck3DTilesViewer(viewport, {
                onStatus: this._setStatus.bind(this),
                debugCollector: this.debugCollector,
                compassElement: this.el("compass"),
                sourceType: this.sourceType,
                sceneType: this.sceneType,
                zeroLevel: this.zeroLevel,
            });
        }

        const {ThreeDViewer} = await import("@/engine/three/ThreeDViewer.js");

        return new ThreeDViewer(viewport, {
            debugCollector: this.debugCollector,
        });
    }

    /**
     * Prevents wheel events inside the 3D viewport from bubbling to the Leaflet map.
     *
     * @param {HTMLElement} element Viewport element that receives scroll events.
     * @returns {void}
     */
    _disableMapScroll(element) {
        if (L?.DomEvent?.disableScrollPropagation) {
            L.DomEvent.disableScrollPropagation(element);
        }

        const stop = L.DomEvent.stopPropagation;
        L.DomEvent
            .on(element, "wheel", stop)
            .on(element, "mousewheel", stop)
            .on(element, "MozMousePixelScroll", stop);
    }

    /**
     * Loads the configured 3D source and passes it to the selected renderer.
     *
     * @returns {Promise<void>} Resolves after loading succeeds or an error message is shown.
     */
    async _loadObject() {
        this._setStatus("Loading 3D object...");

        try {
            if (!this.sourceUrl) {
                throw new Error("3D layer has no source file URL");
            }

            if (this.rendererType === RENDER_TYPE_DECK) {
                const tilesetUrl = normalize3DTilesUrl(this.sourceUrl);
                this._setStatus("Loading 3D Tiles object...");
                await this.viewer.setSource(tilesetUrl);
                return;
            }

            if (this.rendererType === RENDER_TYPE_THREE) {
                const {load3DObject} = await import("@/engine/three/load3DObject.js");

                this.loading = load3DObject(this.sourceUrl, this.sourceType, {
                    onProgress: this._setLoadProgress.bind(this),
                    onDownloadComplete: this._setParseStatus.bind(this),
                    debugStats: this.debugCollector.getStats(),
                });
                const model = await this.loading;
                const bounds = this.viewer.setObject(model);
                this._setStatus(`${model.kind === "mesh" ? "Mesh" : "Point cloud"} loaded: ${model.vertexCount} vertices; bounds ${formatBounds(bounds)}`);
                return
            }
            throw new Error("Can't load object into Unknown render");
        } catch (e) {
            this._setError(e);
        } finally {
            this.loading = null;
        }
    }

    /**
     * Shows a non-error status message in the dialog.
     *
     * @param {string} text Status text.
     * @returns {void}
     */
    _setStatus(text) {
        const status = this.el("status");

        if (status) {
            status.innerText = text;
            status.classList.remove("error");
        }
    }

    /**
     * Shows network download progress for the model file.
     *
     * @param {number} percent Download percentage reported by loaders.gl fetch progress.
     * @param {{loadedBytes?: number, totalBytes?: number}} [progress] Loaded and total byte counters.
     * @returns {void}
     */
    _setLoadProgress(percent, progress = {}) {
        const loaded = formatBytes(progress.loadedBytes);
        const total = formatBytes(progress.totalBytes);
        const suffix = loaded && total ? ` (${loaded} / ${total})` : "";

        this._setStatus(`Downloading 3D object: ${percent}%${suffix}`);
    }

    /**
     * Shows that the network download has finished and loaders.gl is parsing the model.
     *
     * @returns {void}
     */
    _setParseStatus() {
        this._setStatus("Parsing 3D object...");
    }

    /**
     * Shows an error status message in the dialog.
     *
     * @param {Error|string} error Error object or message.
     * @returns {void}
     */
    _setError(error) {
        const status = this.el("status");

        if (status) {
            status.innerText = `3D object loading failed: ${error.message || error}`;
            status.classList.add("error");
        }
    }

    /**
     * Handles the dialog primary action.
     *
     * @returns {boolean} Always true because this component has no primary action.
     */
    onMainAction() {
        return true;
    }

    /**
     * Handles the dialog close/cancel action and releases viewer resources.
     *
     * @returns {boolean} True to allow the dialog to close.
     */
    onCancelAction() {
        this.destroyViewer();
        return true;
    }

    /**
     * Closes the UI component and destroys the active 3D viewer first.
     *
     * @param {boolean} [onCancel=false] Whether the close was initiated as cancel.
     * @returns {void}
     */
    close(onCancel = false) {
        this.destroyViewer();
        super.close(onCancel);
    }

    /**
     * Destroys the active 3D viewer instance if one exists.
     *
     * @returns {void}
     */
    destroyViewer() {
        if (this.debugPanel) {
            this.debugPanel.destroy();
            this.debugPanel = null;
        }
        if (this.toolbar) {
            this.toolbar.destroy();
            this.toolbar = null;
        }

        if (this.viewer) {
            this.viewer.destroy();
            this.viewer = null;
        }
    }

    /**
     * Initializes the structured debug collector panel inside the viewport overlay.
     *
     * @returns {void}
     */
    _initDebugPanel() {
        const debug = this.el("debug");

        if (!debug) {
            return;
        }

        this.debugPanel = new DebugInfoPanel(this.debugCollector);
        this.debugPanel.mount(debug);
    }

    /**
     * Initializes renderer option buttons and connects them to supported viewer methods.
     *
     * @param {object} viewer Active renderer instance.
     * @returns {void}
     */
    _initToolbar(viewer) {
        const toolbarContainer = this.el("toolbar");

        if (!toolbarContainer) {
            return;
        }

        this.toolbar = new UICVerticalToolBar({
            buttons: [
                {
                    id: "resetCamera",
                    icon: "fa fa-arrows-alt",
                    title: "Reset camera to initial scene view",
                    disabled: typeof viewer.resetCameraView !== "function",
                    onClick: () => viewer.resetCameraView?.(),
                },
                {
                    id: "cameraMode",
                    icon: "fa fa-globe",
                    title: "Switch camera between map and orbit interaction",
                    toggle: true,
                    checked: viewer.getCameraMode?.() === CAMERA_MODE.ORBIT,
                    disabled: typeof viewer.setCameraMode !== "function",
                    onChange: (enabled) => viewer.setCameraMode?.(enabled ? CAMERA_MODE.ORBIT : CAMERA_MODE.MAP),
                },
                {
                    id: "referenceGrid",
                    icon: "fas fa-border-all",
                    title: "Show or hide zero level grid",
                    toggle: true,
                    checked: true,
                    disabled: typeof viewer.setReferenceGridVisible !== "function",
                    onChange: (enabled) => viewer.setReferenceGridVisible?.(enabled),
                },
                {
                    id: "boundsBox",
                    icon: "far fa-square",
                    title: "Show or hide scene bounds box",
                    toggle: true,
                    checked: false,
                    disabled: typeof viewer.setBoundsBoxVisible !== "function",
                    onChange: (enabled) => viewer.setBoundsBoxVisible?.(enabled),
                },
                {
                    id: "tileBounds",
                    icon: "fas fa-vector-square",
                    title: "Show or hide selected 3D tile bounds",
                    toggle: true,
                    checked: false,
                    disabled: typeof viewer.setTileBoundsVisible !== "function",
                    onChange: (enabled) => viewer.setTileBoundsVisible?.(enabled),
                },
                {
                    id: "tileStreamingFreeze",
                    icon: "fa fa-pause",
                    title: "Freeze or resume 3D Tiles loading and cache eviction",
                    toggle: true,
                    checked: viewer.getTileStreamingFrozen?.() === true,
                    disabled: typeof viewer.setTileStreamingFrozen !== "function",
                    onChange: (enabled) => viewer.setTileStreamingFrozen?.(enabled),
                },
                {
                    id: "debug",
                    icon: "fa fa-bug",
                    title: "Show or hide debug panel",
                    toggle: true,
                    checked: !this.debugCollapsed,
                    onChange: (enabled) => this._setDebugPanelVisible(enabled),
                },
                {
                    id: "performance",
                    icon: "fa fa-chart-line",
                    title: "Show or hide performance overlay",
                    toggle: true,
                    checked: viewer.getPerformanceOverlayVisible?.() === true,
                    disabled: typeof viewer.setPerformanceOverlayVisible !== "function",
                    onChange: (enabled) => viewer.setPerformanceOverlayVisible?.(enabled),
                },
            ],
        });
        this.toolbar.mount(toolbarContainer);
    }

    /**
     * Shows or hides the debug overlay.
     *
     * @param {boolean} visible Whether the debug overlay should be expanded.
     * @returns {void}
     */
    _setDebugPanelVisible(visible) {
        this.debugCollapsed = !visible;
        this._syncDebugCollapsedState();
        this.debugPanel?.refresh();
    }

    /**
     * Applies the current collapsed or expanded state to the debug overlay DOM.
     *
     * @returns {void}
     */
    _syncDebugCollapsedState() {
        const debug = this.el("debug");

        if (!debug) {
            return;
        }

        debug.classList.toggle("collapsed", this.debugCollapsed);
        debug.title = this.debugCollapsed ? "Use toolbar debug button to expand debug info" : "Use toolbar debug button to collapse debug info";
    }
}

/**
 * Creates the static DOM content used by the 3D viewer dialog.
 *
 * @returns {HTMLElement} Root element containing status and viewport nodes.
 */
function createContent() {
    const root = document.createElement("div");
    root.className = "threeDViewer";
    root.innerHTML = `
        <div class="threeDViewer__status" data-bind="status">Preparing 3D viewer...</div>
        <div class="threeDViewer__viewportFrame" data-bind="viewportFrame">
            <div class="threeDViewer__viewport" data-bind="viewport"></div>
            <div class="threeDViewer__toolbar" data-bind="toolbar"></div>
            <div class="threeDViewer__compass" data-bind="compass"></div>
            <div class="threeDViewer__debug" data-bind="debug"></div>
        </div>
    `;
    return root;
}

/**
 * Formats a THREE.Box3 into a compact "min -> max" string.
 *
 * @param {THREE.Box3|null|undefined} bounds Bounds to format.
 * @returns {string} Human-readable bounds string.
 */
function formatBounds(bounds) {
    if (!bounds) {
        return "unknown";
    }

    return [
        `${formatNumber(bounds.min.x)}, ${formatNumber(bounds.min.y)}, ${formatNumber(bounds.min.z)}`,
        `${formatNumber(bounds.max.x)}, ${formatNumber(bounds.max.y)}, ${formatNumber(bounds.max.z)}`,
    ].join(" -> ");
}

/**
 * Formats a numeric value for status output.
 *
 * @param {number} value Number to format.
 * @returns {string} Fixed two-decimal value, or the raw value converted to string when not finite.
 */
function formatNumber(value) {
    return Number.isFinite(value) ? value.toFixed(2) : String(value);
}

/**
 * Formats byte counters for compact status output.
 *
 * @param {number|null|undefined} value Byte count.
 * @returns {string} Human-readable byte size, or an empty string when value is unknown.
 */
function formatBytes(value) {
    if (!Number.isFinite(value) || value <= 0) {
        return "";
    }

    const units = ["B", "KB", "MB", "GB"];
    let size = value;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }

    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

/**
 * Converts a 3D Tiles directory URL to its tileset.json URL when the backend sends a trailing-slash path.
 *
 * @param {string} sourceUrl Source URL that may be a directory path or a tileset.json file.
 * @returns {string} URL that points to tileset.json.
 */
function normalize3DTilesUrl(sourceUrl) {
    const {path, suffix} = splitUrlPathAndSuffix(sourceUrl);

    if (path.endsWith("/tileset.json")) {
        return sourceUrl;
    }

    if (path.endsWith("/")) {
        return `${path}tileset.json${suffix}`;
    }

    return sourceUrl;
}

/**
 * Splits a URL into its path and query/hash suffix.
 *
 * @param {string} sourceUrl Source URL that may include query string or hash.
 * @returns {{path: string, suffix: string}} Path plus the original suffix.
 */
function splitUrlPathAndSuffix(sourceUrl) {
    const match = sourceUrl.match(/^([^?#]*)(.*)$/);

    return {
        path: match?.[1] || sourceUrl,
        suffix: match?.[2] || "",
    };
}
